import {
  Banner,
  Button,
  Css,
  IconButton,
  Loader,
  LoadingSkeleton,
  Palette,
  ScrollShadows,
  useModal,
  useRightPane,
  useSnackbar,
} from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { createTaskDetailsPageUrl } from "src/RouteUrls";
import { CommentFeed, HistoryFeed, formatDate } from "src/components";
import { DynamicSchedulesSidePaneWithConstraints_PlanTaskFragment } from "src/fragments/circularFragments.generated";
import {
  CommentStreamVisibility,
  Maybe,
  SavePlanTaskInput,
  TradePartnerAvailabilityRequestStatus,
  TradePartnerTaskStatus,
} from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { useDraftScheduleSidePaneQuery } from "src/routes/projects/dynamic-schedules/components/DynamicScheduleSidePane.generated";
import { useSaveDraftPlanScheduleMutation } from "src/routes/projects/dynamic-schedules/draft-mode/ScheduleDraftMode.generated";
import {
  mapToDraftPlanTaskInput,
  useDraftScheduleStore,
} from "src/routes/projects/dynamic-schedules/draft-mode/scheduleDraftStore";
import { Bills } from "src/routes/projects/dynamic-schedules/task-details/Bills";
import { CostAllocations } from "src/routes/projects/dynamic-schedules/task-details/CostAllocations";
import { PurchaseOrders } from "src/routes/projects/dynamic-schedules/task-details/PurchaseOrders";
import {
  ConstraintItemsSection,
  DelayFlagSection,
  MaterialsSection,
} from "src/routes/projects/dynamic-schedules/task-details/SchedulingCard";
import { TaskDetailCard, TaskDetailCardType } from "src/routes/projects/dynamic-schedules/task-details/TaskDetailCard";
import { usePlanTaskDetailsDocumentsQuery } from "src/routes/projects/dynamic-schedules/task-details/TaskDetailsPage.generated";
import { VerificationChecklist } from "src/routes/projects/dynamic-schedules/task-details/VerificationChecklist";
import { TaskBillModalProvider } from "src/routes/projects/schedule-v2/contexts/TaskBillModalContext";
import { TaskDocuments } from "src/routes/projects/schedule-v2/detail-pane/TaskDocuments";
import { pluralize, queryResult } from "src/utils";

export function DynamicScheduleSidePane(props: { draftTaskId: string; onClose: () => void; scheduleParentId: string }) {
  const { draftTaskId, onClose, scheduleParentId } = props;
  const { closeRightPane } = useRightPane();
  const draftTaskChanges = useDraftScheduleStore((state) => state.draftTaskChanges);
  const userAddedScheduleFlags = useDraftScheduleStore((state) => state.userAddedScheduleFlags);
  const skipStaleTaskAutoPin = useDraftScheduleStore((state) => state.skipStaleTaskAutoPin);
  const currentDate = useDraftScheduleStore((state) => state.currentDate);
  const isNewTask = draftTaskId.includes("WIP");
  const isDeletedTask = useMemo(
    () => draftTaskChanges.some((change) => change.id === draftTaskId && change?.delete),
    [draftTaskId, draftTaskChanges],
  );

  const query = useDraftScheduleSidePaneQuery({
    variables: {
      input: {
        scheduleParentId,
        draftTaskChanges: mapToDraftPlanTaskInput(draftTaskChanges, userAddedScheduleFlags),
        taskDetailId: draftTaskId,
        skipStaleTaskAutoPin,
        currentDate,
      },
      taskId: draftTaskId,
    },
    skip: isNewTask || isDeletedTask,
  });

  // If a user is clicky clicky and opens the pane on a task marked for deletion immediately close it.
  useEffect(() => {
    isDeletedTask && closeRightPane();
  }, [closeRightPane, isDeletedTask]);

  return queryResult(query, {
    data: (data) => (
      <DynamicScheduleSidePaneView
        planTask={data.draftPlanSchedule.planTaskDetail}
        isNewTask={isNewTask}
        onClose={onClose}
        scheduleParentId={scheduleParentId}
        loading={query.loading}
      />
    ),
    loading: () => (
      <div css={Css.df.fdc.gap7.p3.$}>
        <LoadingSkeleton columns={1} rows={15} />
      </div>
    ),
  });
}

function DynamicScheduleSidePaneView(props: {
  planTask: Maybe<DynamicSchedulesSidePaneWithConstraints_PlanTaskFragment>;
  isNewTask: boolean;
  onClose: () => void;
  scheduleParentId: string;
  loading: boolean;
}) {
  const { planTask, isNewTask, onClose, scheduleParentId, loading } = props;
  const history = useHistory();
  const { closeRightPane } = useRightPane();
  const reset = useDraftScheduleStore((state) => state.reset);

  if (!planTask || isNewTask)
    return (
      <div css={Css.br8.bsDashed.bcGray200.gray700.bw("3px").py2.df.jcc.my2.$}>
        Task details available after publishing
      </div>
    );

  return (
    <TaskBillModalProvider>
      <div css={Css.df.fdc.h100.oa.px3.pb5.bgWhite.z0.gap1.$}>
        <div css={Css.df.jcsb.aic.sticky.pt3.top0.bgWhite.z1.$}>
          <div css={Css.df.aic.gap1.xl.pb1.$}>{planTask.name}</div>
          <div css={Css.df.gap1.$}>
            {loading ? (
              <Loader size="xs" />
            ) : (
              <>
                <IconButton
                  tooltip={isNewTask ? "Task details available after publishing" : "View full page details"}
                  icon="expand"
                  color={isNewTask ? Palette.Gray400 : Palette.Gray900}
                  disabled={isNewTask}
                  // Note: IconButton should support just a string for onclick navigation since it's built on Button, but it
                  // doesn't work
                  onClick={() => history.push(createTaskDetailsPageUrl(scheduleParentId, planTask.id))}
                />
                <IconButton
                  icon="x"
                  color={Palette.Gray900}
                  onClick={() => {
                    onClose();
                    closeRightPane();
                  }}
                />
              </>
            )}
          </div>
        </div>
        {planTask.tradePartner?.name && <div css={Css.md.gray700.$}>{planTask.tradePartner?.name}</div>}
        <div css={Css.md.gray700.$}>
          {formatDate(planTask.startDate)} - {formatDate(planTask.endDate)} (
          {planTask.knownDurationInDays ?? planTask.durationInDays}
          {pluralize(planTask.knownDurationInDays ?? planTask.durationInDays, " day")})
        </div>

        <div css={Css.df.fdc.gap7.pt2.$}>
          <TradePartnerAvailabilityRequestRescheduleSection
            planTask={planTask}
            scheduleParentId={scheduleParentId}
            onClose={() => {
              onClose();
              closeRightPane();
              reset();
            }}
          />
          <SidePaneCommentSection planTask={planTask} />
          <PurchaseOrders projectItems={planTask.projectItems} inSidePane />
          <Bills task={planTask} inSidePane />
          <SidePaneDocumentsSection planTask={planTask} />
          <MaterialsSection task={planTask} inSidePane />
          <DelayFlagSection task={planTask} inSidePane />
          <CostAllocations projectItems={planTask.projectItems} inSidePane />
          <VerificationChecklist planTask={planTask} />
          <ConstraintItemsSection task={planTask} inSidePane type="constraints" />
          <ConstraintItemsSection task={planTask} inSidePane type="allowances" />
          <SidePaneHistoryCard planTask={planTask} />
        </div>
      </div>
    </TaskBillModalProvider>
  );
}

// We don't really need form state here, but it's easier to just bite that bullet and pray we can refactor it later to have a
// version not weighed down by old code
function SidePaneDocumentsSection({
  planTask,
}: {
  planTask: DynamicSchedulesSidePaneWithConstraints_PlanTaskFragment;
}) {
  const { data } = usePlanTaskDetailsDocumentsQuery({ variables: { parentId: planTask.schedule.parent.id } });
  const formState = useFormState<SavePlanTaskInput, DynamicSchedulesSidePaneWithConstraints_PlanTaskFragment>({
    config: { id: { type: "value" }, documents: { type: "value" } },
    init: { input: planTask, map: (task) => ({ id: task.id, documents: task.documents.map((doc) => doc.id) }) },
  });
  return (
    <TaskDocuments
      parentId={planTask.schedule.parent.id}
      documentTypes={data?.documentTypes ?? []}
      documents={data?.documents ?? []}
      formState={formState}
      task={planTask}
      onPlanTask
      taskStatus={planTask.status.code}
    />
  );
}

function SidePaneHistoryCard({ planTask }: { planTask: DynamicSchedulesSidePaneWithConstraints_PlanTaskFragment }) {
  return (
    <TaskDetailCard noPadding cardType={TaskDetailCardType.History} maxHeight={450}>
      <h2 css={Css.lg.$}>History</h2>
      <ScrollShadows xss={Css.h100.oys.$}>
        <HistoryFeed historyItems={planTask?.history ?? []} renderAsCard={false} />
      </ScrollShadows>
    </TaskDetailCard>
  );
}

function SidePaneCommentSection({ planTask }: { planTask: DynamicSchedulesSidePaneWithConstraints_PlanTaskFragment }) {
  return (
    <TaskDetailCard cardType={TaskDetailCardType.Comments} noPadding maxHeight={650}>
      <CommentFeed
        key={planTask.id}
        inlineCommentTitle={<h2 css={Css.mdSb.$}>Comments</h2>}
        showCommentTitle={false}
        showFollowers={false}
        commentable={planTask}
        maxHeight={490}
        initialTab={CommentStreamVisibility.Trades}
      />
    </TaskDetailCard>
  );
}

function TradePartnerAvailabilityRequestRescheduleSection({
  planTask,
  scheduleParentId,
  onClose,
}: {
  planTask: DynamicSchedulesSidePaneWithConstraints_PlanTaskFragment;
  scheduleParentId: string;
  onClose: () => void;
}) {
  const { triggerNotice } = useSnackbar();
  const [showRescheduledBanner, setShowRescheduledBanner] = useState(false);
  const draftTaskChanges = useDraftScheduleStore((state) => state.draftTaskChanges);
  const currentDate = useDraftScheduleStore((state) => state.currentDate);
  // find the latest request on the task
  const latestRequest = planTask.tradePartnerAvailabilityRequests?.last;
  const tradePartnerName = planTask.tradePartner?.name;
  const rescheduleDate = latestRequest?.rescheduleDates?.last;
  const { openModal } = useModal();
  const [saveDraftSchedule] = useSaveDraftPlanScheduleMutation();

  useEffect(() => {
    if (
      latestRequest?.status.code === TradePartnerAvailabilityRequestStatus.RescheduleNeeded &&
      latestRequest?.rescheduleDates?.nonEmpty
    )
      setShowRescheduledBanner(true);
  }, [latestRequest]);

  // publish the reschedule date and close out of draft mode
  const onRescheduleAction = useCallback(async () => {
    openModal({
      content: (
        <ConfirmationModal
          title="Are you sure you want to Publish?"
          label="Publish Schedule"
          onConfirmAction={async () => {
            await saveDraftSchedule({
              variables: {
                input: {
                  scheduleParentId,
                  draftTaskChanges: [
                    {
                      id: planTask.id,
                      earliestStartDate: rescheduleDate,
                      isManuallyScheduled: true,
                      tradePartnerStatus: TradePartnerTaskStatus.Confirmed,
                    },
                  ],
                  tradePartnerAvailabilityRequests: [
                    {
                      taskId: planTask.id,
                      confirmRequestedReschedule: true,
                      tradePartnerId: planTask.tradePartner?.id,
                      tradePartnerContactIds: planTask.knownSchedulingContacts.map((c) => c.id),
                    },
                  ],
                  currentDate,
                },
              },
              refetchQueries: ["DraftPlanSchedule"],
            });
            triggerNotice({
              message: `You accepted the trade's new date of ${formatDate(rescheduleDate?.date, "monthShort")}.`,
            });
            onClose();
          }}
          confirmationMessage="Publishing will accept the simulated schedule changes and apply them to the existing schedule. Are you sure you want to continue?"
        />
      ),
    });
  }, [currentDate, planTask, rescheduleDate, openModal, saveDraftSchedule, scheduleParentId, triggerNotice, onClose]);

  // if we don't have a request, then don't show this section
  if (planTask.tradePartnerAvailabilityRequests?.isEmpty) return null;

  return (
    <>
      {showRescheduledBanner && (
        <Banner
          type="info"
          showIcon={false}
          message={
            <div css={Css.df.fdc.gap2.$}>
              <div>
                {`${tradePartnerName} requested to reschedule this task with a new date of ${formatDate(rescheduleDate?.date, "monthShort")}. Would you like to accept this date and notify ${tradePartnerName}?`}
              </div>
              <Button
                label="Confirm New Date"
                onClick={onRescheduleAction}
                variant="text"
                disabled={draftTaskChanges.nonEmpty && "Publish your schedule changes before accepting the new date."}
              />
            </div>
          }
          onClose={() => setShowRescheduledBanner(false)}
        />
      )}
    </>
  );
}
